import { APP } from '@/config/app';

/**
 * Build a wa.me deep-link. The optional `phone` override lets callers send the
 * link to a specific contact (e.g. a buyer in the admin follow-up flow) — when
 * omitted, falls back to the public DDM number (`APP.whatsappNumber`).
 *
 * The phone is sanitized to digits-only because `wa.me` rejects URLs that
 * contain `+`, spaces or other separators — the DB may store phones in any of
 * those formats, so we normalize at the URL boundary rather than at write
 * time.
 */
export function getWhatsAppHref(message?: string, phone?: string): string {
  const number = (phone ?? APP.whatsappNumber).replace(/\D/g, '');
  const base = `https://wa.me/${number}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

/** Minimal shape needed to qualify an advisor for the WhatsApp cascade. */
type WhatsAppAdvisor = { is_active: boolean; phone: string | null } | null | undefined;

/**
 * Pick the WhatsApp phone for a deal, walking the cascade in order (assigned
 * advisor → default advisor → …). An advisor only qualifies if it is active AND
 * carries a dialable phone (at least one digit once sanitized). Returns
 * `undefined` when none qualify, so `getWhatsAppHref` falls back to the agency
 * number (`APP.whatsappNumber`).
 */
export function pickAdvisorWhatsAppPhone(...advisors: WhatsAppAdvisor[]): string | undefined {
  for (const advisor of advisors) {
    if (advisor?.is_active && advisor.phone && advisor.phone.replace(/\D/g, '').length > 0) {
      return advisor.phone;
    }
  }
  return undefined;
}
